import { useState, useEffect, useRef } from "react";
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  ShoppingCart, Plus, Search, Send, Upload, Loader2, CheckCircle2,
  X, Save, RefreshCw, Clock, Truck, Package, ChevronRight,
  FileText, Trash2, Edit, Mail, Eye, MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending:    { label: "En attente",   color: "bg-amber-500/20 text-amber-400",   icon: Clock },
  confirmed:  { label: "Confirmée",    color: "bg-blue-500/20 text-blue-400",     icon: CheckCircle2 },
  processing: { label: "En cours",     color: "bg-violet-500/20 text-violet-400", icon: Package },
  shipped:    { label: "Expédiée",     color: "bg-cyan-500/20 text-cyan-400",     icon: Truck },
  delivered:  { label: "Livrée",       color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2 },
  cancelled:  { label: "Annulée",      color: "bg-red-500/20 text-red-400",       icon: X },
};

const DOC_TYPE_LABEL = { commande: "Commande", devis: "Devis", facture: "Facture" };

const EMPTY_FORM = {
  client_name: "", client_email: "", client_phone: "", client_address: "",
  client_code: "", doc_type: "commande", doc_number: "", doc_date: "",
  items: [{ name: "", quantity: 1, unit_price: 0, remise: 0, total_line: 0 }],
  total: 0, payment_mode: "", notes: "", source: "manual",
};

export default function OrdersPage() {
  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [sending, setSending]       = useState(false);
  const [parsing, setParsing]       = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const fileRef = useRef(null);

  const token = () => localStorage.getItem("token");
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/orders`, { headers: headers() });
      setOrders(res.data);
    } catch { toast.error("Erreur chargement commandes"); }
    finally { setLoading(false); }
  };

  const calcTotal = (items) =>
    items.reduce((s, i) => s + (parseFloat(i.total_line) || (parseFloat(i.unit_price)||0) * (parseFloat(i.quantity)||1)), 0);

  const updateItem = (idx, field, val) => {
    const newItems = form.items.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: val };
      if (field === "unit_price" || field === "quantity" || field === "remise") {
        const price = parseFloat(updated.unit_price) || 0;
        const qty   = parseFloat(updated.quantity) || 1;
        const remise = parseFloat(updated.remise) || 0;
        updated.total_line = parseFloat((price * qty * (1 - remise/100)).toFixed(2));
      }
      return updated;
    });
    const total = calcTotal(newItems);
    setForm(f => ({ ...f, items: newItems, total }));
  };

  const addItem = () => setForm(f => ({
    ...f,
    items: [...f.items, { name: "", quantity: 1, unit_price: 0, remise: 0, total_line: 0 }]
  }));

  const removeItem = (idx) => {
    const newItems = form.items.filter((_, i) => i !== idx);
    setForm(f => ({ ...f, items: newItems, total: calcTotal(newItems) }));
  };

  // Import PDF Klick Stock
  const handlePDFUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API_URL}/api/orders/parse-pdf`, fd, {
        headers: { ...headers(), "Content-Type": "multipart/form-data" }
      });
      const data = res.data;
      if (data.error) { toast.error("Erreur lecture PDF: " + data.error); return; }
      setParsedData(data);
      setForm({
        ...EMPTY_FORM,
        client_name:    data.client_name    || "",
        client_address: data.client_address || "",
        client_code:    data.client_code    || "",
        doc_type:       data.doc_type       || "commande",
        doc_number:     data.doc_number     || "",
        doc_date:       data.doc_date       || "",
        items:          data.items?.length > 0 ? data.items : EMPTY_FORM.items,
        total:          data.total          || 0,
        payment_mode:   data.payment_mode   || "",
        source:         "klick_stock",
      });
      toast.success(`✅ PDF importé ! ${data.items?.length || 0} articles extraits.`);
      setCreateOpen(true);
    } catch (err) {
      toast.error("Erreur import PDF: " + (err.response?.data?.detail || err.message));
    } finally { setParsing(false); e.target.value = ""; }
  };

  const handleSave = async () => {
    if (!form.client_name) { toast.error("Nom client requis"); return; }
    setSaving(true);
    try {
      if (selected?.id) {
        await axios.put(`${API_URL}/api/orders/${selected.id}`, form, { headers: headers() });
        toast.success("Commande mise à jour");
      } else {
        await axios.post(`${API_URL}/api/orders`, form, { headers: headers() });
        toast.success("Commande créée ✅");
      }
      setCreateOpen(false);
      setSelected(null);
      setForm(EMPTY_FORM);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur sauvegarde");
    } finally { setSaving(false); }
  };

  const handleWhatsApp = (order) => {
    if (!order.client_phone) { toast.error("Numéro de téléphone client manquant"); return; }
    const phone = order.client_phone.replace(/\D/g, "");
    const intlPhone = phone.startsWith("0") ? "596" + phone.slice(1) : phone;
    const trackingUrl = `https://app.amcphone-martinique.fr/suivi-commande/${order.public_token || ""}`;
    const repairUrl = order.repair_id ? `https://app.amcphone-martinique.fr/track/` : "";
    const items_summary = (order.items || []).slice(0, 3).map(i => `• ${i.name} x${i.quantity}`).join("\n");
    const docLabel = order.doc_type === "facture" ? "Facture" : order.doc_type === "devis" ? "Devis" : "Commande";
    const msg = `Bonjour ${order.client_name || ""},`
      + `\n\nVotre ${docLabel.toLowerCase()} *${order.doc_number || order.order_number}* d'un montant de *${parseFloat(order.total || 0).toFixed(2)} €* a bien été enregistrée.`
      + (items_summary ? `\n\n${items_summary}` : "")
      + `\n\nSuivez l'avancement de votre commande ici :`
      + `\n${trackingUrl}`
      + (repairUrl ? `\n\nSuivi réparation :\n${repairUrl}` : "")
      + `\n\nPour toute question, n'hésitez pas à nous contacter.`
      + `\n\n— AMCPhone Martinique`;
    const url = `https://wa.me/${intlPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const handleSendConfirmation = async (order) => {
    if (!order.client_email) { toast.error("Email client manquant"); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/orders/${order.id}/send-confirmation`, {}, { headers: headers() });
      toast.success("Email de confirmation envoyé ✅");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur envoi");
    } finally { setSending(false); }
  };

  const handleUpdateStatus = async (order, status) => {
    try {
      await axios.put(`${API_URL}/api/orders/${order.id}`, { status }, { headers: headers() });
      toast.success(`Statut mis à jour : ${STATUS_CONFIG[status]?.label}`);
      fetchOrders();
      if (selected?.id === order.id) setSelected(prev => ({ ...prev, status }));
    } catch { toast.error("Erreur mise à jour statut"); }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Supprimer la commande ${order.order_number} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/orders/${order.id}`, { headers: headers() });
      toast.success("Commande supprimée");
      setDetailOpen(false);
      fetchOrders();
    } catch { toast.error("Erreur suppression"); }
  };

  const filtered = orders.filter(o => {
    const matchSearch = !search || [o.order_number, o.client_name, o.doc_number, o.client_email]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = filterStatus === "all" || o.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-chivo text-2xl font-bold text-foreground flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-primary" />
              Commandes
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">{orders.length} commande{orders.length > 1 ? "s" : ""}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Import PDF */}
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handlePDFUpload} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={parsing}
              className="text-xs border-violet-500/40 text-violet-400 hover:bg-violet-500/10"
            >
              {parsing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              Importer PDF Klick Stock
            </Button>
            <Button onClick={() => { setForm(EMPTY_FORM); setSelected(null); setCreateOpen(true); }} className="bg-primary text-xs">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvelle commande
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const count = orders.filter(o => o.status === key).length;
            return (
              <button
                key={key}
                onClick={() => setFilterStatus(filterStatus === key ? "all" : key)}
                className={`p-2 rounded-xl border text-left transition-all ${filterStatus === key ? "border-primary/50 bg-primary/10" : "bg-card border-border hover:border-border/80"}`}
              >
                <p className={`text-xs px-2 py-0.5 rounded-full font-medium inline-block mb-1 ${cfg.color}`}>{cfg.label}</p>
                <p className="text-lg font-bold text-foreground">{count}</p>
              </button>
            );
          })}
        </div>

        {/* Recherche */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher une commande..." className="pl-9 bg-secondary/50" />
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucune commande</p>
            <p className="text-xs mt-1">Importez un PDF Klick Stock ou créez une commande manuellement</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(order => {
              const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              return (
                <Card key={order.id} className="bg-card border-border hover:border-primary/30 cursor-pointer transition-all"
                  onClick={() => { setSelected(order); setDetailOpen(true); }}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <ShoppingCart className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-foreground">{order.order_number}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                            {order.doc_number && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                                {DOC_TYPE_LABEL[order.doc_type] || "Doc"} {order.doc_number}
                              </span>
                            )}
                            {order.source === "klick_stock" && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400">Klick Stock</span>
                            )}
                          </div>
                          <p className="text-sm text-foreground mt-0.5">{order.client_name || "Client non renseigné"}</p>
                          <p className="text-xs text-muted-foreground">{order.items?.length || 0} article{(order.items?.length||0) > 1 ? "s" : ""} • {new Date(order.created_at).toLocaleDateString("fr-FR")}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-primary">{parseFloat(order.total||0).toFixed(2)}€</p>
                        {order.confirmation_sent && <p className="text-xs text-emerald-400 mt-0.5">✅ Confirmé</p>}
                        <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto mt-1" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog Créer/Éditer */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              {selected ? "Modifier la commande" : "Nouvelle commande"}
              {form.source === "klick_stock" && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400">Klick Stock</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Type et numéro doc */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Type</label>
                <select value={form.doc_type} onChange={e => setForm(f => ({...f, doc_type: e.target.value}))}
                  className="w-full h-10 px-3 rounded-xl bg-secondary/50 border border-border text-sm text-foreground">
                  <option value="commande">Commande</option>
                  <option value="devis">Devis</option>
                  <option value="facture">Facture</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">N° document</label>
                <Input value={form.doc_number} onChange={e => setForm(f => ({...f, doc_number: e.target.value}))} placeholder="CD24/0113" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Date document</label>
                <Input value={form.doc_date} onChange={e => setForm(f => ({...f, doc_date: e.target.value}))} placeholder="13/11/2024" />
              </div>
            </div>

            {/* Client */}
            <div className="p-3 rounded-xl bg-secondary/20 border border-border/50">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Client</p>
              <div className="grid grid-cols-2 gap-2">
                <Input value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} placeholder="Nom complet *" />
                <Input value={form.client_code} onChange={e => setForm(f => ({...f, client_code: e.target.value}))} placeholder="Code client (CL0090)" />
                <Input value={form.client_email} onChange={e => setForm(f => ({...f, client_email: e.target.value}))} placeholder="Email" type="email" />
                <Input value={form.client_phone} onChange={e => setForm(f => ({...f, client_phone: e.target.value}))} placeholder="Téléphone" />
                <Input value={form.client_address} onChange={e => setForm(f => ({...f, client_address: e.target.value}))} placeholder="Adresse" className="col-span-2" />
              </div>
            </div>

            {/* Articles */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Articles</label>
                <Button variant="outline" size="sm" onClick={addItem} className="text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {/* En-tête */}
                <div className="grid grid-cols-12 gap-1 text-xs text-muted-foreground px-1">
                  <span className="col-span-5">Désignation</span>
                  <span className="col-span-1 text-center">Qté</span>
                  <span className="col-span-2 text-right">P.U €</span>
                  <span className="col-span-2 text-right">Rem %</span>
                  <span className="col-span-1 text-right">Total</span>
                  <span className="col-span-1"></span>
                </div>
                {form.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-center">
                    <Input value={item.name} onChange={e => updateItem(i, "name", e.target.value)} placeholder="Désignation" className="col-span-5 text-xs h-8" />
                    <Input value={item.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} type="number" min="1" className="col-span-1 text-xs h-8 text-center" />
                    <Input value={item.unit_price} onChange={e => updateItem(i, "unit_price", e.target.value)} type="number" step="0.01" className="col-span-2 text-xs h-8 text-right" />
                    <Input value={item.remise} onChange={e => updateItem(i, "remise", e.target.value)} type="number" min="0" max="100" placeholder="0" className="col-span-2 text-xs h-8 text-right" />
                    <span className="col-span-1 text-xs text-right font-medium text-primary">{parseFloat(item.total_line||0).toFixed(2)}€</span>
                    <button onClick={() => removeItem(i)} className="col-span-1 text-red-400 hover:text-red-300 flex justify-center">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center p-3 rounded-xl bg-primary/10 border border-primary/20">
              <span className="font-bold text-foreground">NET À PAYER</span>
              <span className="font-bold text-primary text-lg">{parseFloat(form.total||0).toFixed(2)}€</span>
            </div>

            {/* Options */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Mode de règlement</label>
                <Input value={form.payment_mode} onChange={e => setForm(f => ({...f, payment_mode: e.target.value}))} placeholder="Espèces, CB, Virement..." />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                <Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Informations complémentaires..." />
              </div>
            </div>

            <Button className="w-full bg-primary" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {selected ? "Mettre à jour" : "Créer la commande"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Détail */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              {selected?.order_number}
            </DialogTitle>
          </DialogHeader>
          {selected && (() => {
            const cfg = STATUS_CONFIG[selected.status] || STATUS_CONFIG.pending;
            return (
              <div className="space-y-4">
                {/* Statut */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm px-3 py-1 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                  {selected.doc_number && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                      {DOC_TYPE_LABEL[selected.doc_type]} {selected.doc_number}
                    </span>
                  )}
                  {selected.confirmation_sent && <span className="text-xs text-emerald-400">✅ Confirmation envoyée</span>}
                </div>

                {/* Client */}
                <div className="p-3 rounded-xl bg-secondary/30">
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Client</p>
                  <p className="text-sm font-medium">{selected.client_name}</p>
                  {selected.client_code && <p className="text-xs text-muted-foreground">Code: {selected.client_code}</p>}
                  {selected.client_email && <p className="text-xs text-muted-foreground">{selected.client_email}</p>}
                  {selected.client_phone && <p className="text-xs text-muted-foreground">{selected.client_phone}</p>}
                  {selected.client_address && <p className="text-xs text-muted-foreground">{selected.client_address}</p>}
                </div>

                {/* Articles */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Articles</p>
                  <div className="space-y-1">
                    {(selected.items||[]).map((item, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-border last:border-0">
                        <span className="text-foreground flex-1">{item.name} <span className="text-muted-foreground">×{item.quantity}</span>
                          {item.remise > 0 && <span className="text-amber-400 ml-1">-{item.remise}%</span>}
                        </span>
                        <span className="text-primary font-medium ml-2">{parseFloat(item.total_line||0).toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between font-bold mt-2 pt-2 border-t border-border text-base">
                    <span>NET À PAYER</span>
                    <span className="text-primary">{parseFloat(selected.total||0).toFixed(2)}€</span>
                  </div>
                  {selected.payment_mode && (
                    <p className="text-xs text-muted-foreground mt-1">Mode: {selected.payment_mode}</p>
                  )}
                </div>

                {/* Changer statut */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Mettre à jour le statut</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <button
                        key={key}
                        onClick={() => handleUpdateStatus(selected, key)}
                        className={`text-xs px-2 py-1.5 rounded-lg font-medium transition-all border ${
                          selected.status === key
                            ? `${cfg.color} border-current`
                            : "bg-secondary/30 text-muted-foreground border-border hover:border-primary/30"
                        }`}
                      >
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleSendConfirmation(selected)}
                    disabled={sending || !selected.client_email}
                    className="text-xs bg-emerald-600 hover:bg-emerald-700"
                    size="sm"
                  >
                    {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                    Email confirmation
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleWhatsApp(selected)}
                    className="text-xs bg-[#25D366] hover:bg-[#20ba58] text-white"
                  >
                    <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
                    WhatsApp
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => { setForm({...selected}); setCreateOpen(true); }}
                    className="text-xs col-span-2"
                  >
                    <Edit className="w-3.5 h-3.5 mr-1.5" /> Modifier
                  </Button>
                </div>

                <Button
                  variant="outline" size="sm"
                  onClick={() => handleDelete(selected)}
                  className="w-full text-xs border-red-500/40 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
